import { Web3DViewer } from '@technology/web3d';
import { TrimbimModel } from '@technology/web3d-plugin-trimbim';
import {
  getTrbModelId,
  isTrbModelName,
} from '../trim-bim/trim-bim-model-name-helpers';

/**
 * Current 3D selection grouped first by viewer, then by TRB model runtime id.
 * - outer key: `Web3DViewer`
 * - inner key: model runtime id
 * - inner value: selected runtime entity ids in that model
 */
export type SelectedByViewerMap = Map<Web3DViewer, Map<string, number[]>>;

export type ObjectsSelection = Array<{
  fileId: string;
  versionId: string;
  objectIds: string[];
  entityIds?: number[];
}>;

/**
 * Accumulates valid selection per version while building union selection.
 * Both sets are built from matched (objectId, entityId) pairs only.
 */
interface IVersionSelectionAccumulator {
  fileId: string;
  versionId: string;
  objectIds: Set<string>;
  entityIds: Set<number>;
}

interface IResolvedSelection {
  objectId: string;
  entityId: number;
}

export async function buildSelectionUnion(
  selectedByViewer: SelectedByViewerMap,
  getPersistentObjectIds: (
    model: TrimbimModel,
    entityIds: number[],
    signal?: AbortSignal
  ) => Promise<string[]>,
  signal?: AbortSignal
): Promise<ObjectsSelection> {
  signal?.throwIfAborted();
  const byVersion = new Map<string, IVersionSelectionAccumulator>();

  for (const [viewer, selectionByModel] of selectedByViewer) {
    signal?.throwIfAborted();
    for (const [modelId, entityIds] of selectionByModel) {
      signal?.throwIfAborted();
      if (!isTrbModelName(modelId) || entityIds.length === 0) continue;
      const model = viewer.getModel(modelId);
      if (!(model instanceof TrimbimModel)) continue;

      const trajectoryId = getTrbModelId(modelId);
      if (!trajectoryId) continue;

      const objectIds = await getPersistentObjectIds(model, entityIds, signal);
      const resolvedSelections = toResolvedSelections(objectIds, entityIds);
      if (resolvedSelections.length === 0) continue;

      const existing = byVersion.get(trajectoryId) ?? {
        fileId: trajectoryId,
        versionId: trajectoryId,
        objectIds: new Set<string>(),
        entityIds: new Set<number>(),
      };

      resolvedSelections.forEach(({ objectId, entityId }) => {
        existing.objectIds.add(objectId);
        existing.entityIds.add(entityId);
      });
      byVersion.set(trajectoryId, existing);
    }
  }

  return [...byVersion.values()].map((item) => ({
    fileId: item.fileId,
    versionId: item.versionId,
    objectIds: [...item.objectIds],
    entityIds: [...item.entityIds],
  }));
}

function toResolvedSelections(
  objectIds: string[],
  entityIds: number[]
): IResolvedSelection[] {
  const resolvedSelections: IResolvedSelection[] = [];
  for (let i = 0; i < objectIds.length; i++) {
    const objectId = objectIds[i];
    const entityId = entityIds[i];
    // eslint-disable-next-line sonarjs/different-types-comparison -- index access can be undefined at runtime (noUncheckedIndexedAccess is off)
    if (!objectId || entityId === undefined) continue;
    resolvedSelections.push({ objectId, entityId });
  }
  return resolvedSelections;
}
